import { useState, useRef, useEffect, useCallback } from "react";
import { Camera, X, Loader2, Search, ScanBarcode } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface FoodProduct {
  name: string;
  brand?: string;
  calories: number;
  protein_g: number;
  carbs_g: number;
  fat_g: number;
  serving_size?: string;
  image_url?: string;
  barcode: string;
}

interface BarcodeScannerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onProductFound: (product: FoodProduct) => void;
}

export function BarcodeScanner({ open, onOpenChange, onProductFound }: BarcodeScannerProps) {
  const [isScanning, setIsScanning] = useState(false);
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [manualBarcode, setManualBarcode] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<FoodProduct[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // Check if Barcode Detection API is available
  const hasBarcodeAPI = typeof window !== 'undefined' && 'BarcodeDetector' in window;

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setIsScanning(false);
  }, []);

  const lookupBarcode = async (barcode: string) => {
    setIsLookingUp(true);
    try {
      const { data, error } = await supabase.functions.invoke('food-lookup', {
        body: { barcode },
      });

      if (error) throw error;

      if (data.found && data.product) {
        onProductFound(data.product);
        onOpenChange(false);
        toast.success(`Found: ${data.product.name}`);
      } else {
        toast.error('Product not found in database');
      }
    } catch (error) {
      console.error('Barcode lookup error:', error);
      toast.error('Failed to lookup product');
    } finally {
      setIsLookingUp(false);
    }
  };

  const startCamera = async () => {
    setCameraError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
      });
      streamRef.current = stream;
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      
      setIsScanning(true);

      // Start barcode detection if available
      if (hasBarcodeAPI) {
        const BarcodeDetector = (window as any).BarcodeDetector;
        const detector = new BarcodeDetector({
          formats: ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128', 'code_39'],
        });

        const detectBarcodes = async () => {
          if (!videoRef.current || !streamRef.current) return;
          
          try {
            const barcodes = await detector.detect(videoRef.current);
            if (barcodes.length > 0) {
              const barcode = barcodes[0].rawValue;
              stopCamera();
              await lookupBarcode(barcode);
              return;
            }
          } catch (e) {
            // Detection failed, continue scanning
          }
          
          if (streamRef.current) {
            requestAnimationFrame(detectBarcodes);
          }
        };

        detectBarcodes();
      }
    } catch (error) {
      console.error('Camera error:', error);
      setCameraError('Could not access camera. Please enter barcode manually.');
    }
  };

  const handleManualSubmit = async () => {
    if (!manualBarcode.trim()) return;
    await lookupBarcode(manualBarcode.trim());
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setIsSearching(true);
    try {
      const { data, error } = await supabase.functions.invoke('food-lookup', {
        body: { search: searchQuery },
      });

      if (error) throw error;
      setSearchResults(data.products || []);
      
      if (data.products?.length === 0) {
        toast.info('No products found');
      }
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Search failed');
    } finally {
      setIsSearching(false);
    }
  };

  useEffect(() => {
    if (!open) {
      stopCamera();
      setManualBarcode('');
      setSearchQuery('');
      setSearchResults([]);
      setCameraError(null);
    }
  }, [open, stopCamera]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ScanBarcode className="w-5 h-5 text-primary" />
            Scan Food Barcode
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Camera scanning */}
          {isScanning ? (
            <div className="relative">
              <video
                ref={videoRef}
                className="w-full rounded-lg bg-muted"
                playsInline
                muted
              />
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-3/4 h-1/3 border-2 border-primary rounded-lg" />
              </div>
              <Button
                variant="outline"
                size="sm"
                className="absolute top-2 right-2"
                onClick={stopCamera}
              >
                <X className="w-4 h-4" />
              </Button>
              {!hasBarcodeAPI && (
                <p className="text-xs text-muted-foreground text-center mt-2">
                  Barcode detection not supported. Please enter barcode manually.
                </p>
              )}
            </div>
          ) : (
            <Button
              onClick={startCamera}
              className="w-full gap-2"
              disabled={isLookingUp}
            >
              <Camera className="w-4 h-4" />
              Open Camera
            </Button>
          )}

          {cameraError && (
            <p className="text-sm text-destructive text-center">{cameraError}</p>
          )}

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">or enter manually</span>
            </div>
          </div>

          {/* Manual barcode entry */}
          <div className="flex gap-2">
            <Input
              placeholder="Enter barcode number..."
              value={manualBarcode}
              onChange={(e) => setManualBarcode(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleManualSubmit()}
            />
            <Button
              onClick={handleManualSubmit}
              disabled={isLookingUp || !manualBarcode.trim()}
            >
              {isLookingUp ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Lookup'}
            </Button>
          </div>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">or search by name</span>
            </div>
          </div>

          {/* Search by name */}
          <div className="flex gap-2">
            <Input
              placeholder="Search food name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
            <Button
              onClick={handleSearch}
              disabled={isSearching || !searchQuery.trim()}
            >
              {isSearching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            </Button>
          </div>

          {/* Search results */}
          {searchResults.length > 0 && (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {searchResults.map((product, idx) => (
                <button
                  key={`${product.barcode}-${idx}`}
                  onClick={() => {
                    onProductFound(product);
                    onOpenChange(false);
                  }}
                  className="w-full flex items-center gap-3 p-3 rounded-lg border hover:bg-muted/50 transition-colors text-left"
                >
                  {product.image_url && (
                    <img
                      src={product.image_url}
                      alt={product.name}
                      className="w-10 h-10 object-cover rounded"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{product.name}</p>
                    {product.brand && (
                      <p className="text-xs text-muted-foreground truncate">{product.brand}</p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      {product.calories} cal • {product.protein_g}g P • {product.carbs_g}g C • {product.fat_g}g F
                    </p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
